import React, { useEffect } from "react";
import { DateTime } from "luxon";
import useStore from "../../context/useStore";
import { repurchaseActions } from "../../context/actions/repurchaseActions";
import Control from "./Control";
import InputGroupWithResetValue from "./InputGroupWithResetValue";
import Loader from "./Loader";
import useTVA from "../../hooks/useInit/useTVA";
import useSimulatedRateVR from "../../hooks/useSimulatedRateVR";

const formatDate = (iso) =>
  iso ? DateTime.fromISO(iso).toFormat("dd/MM/yyyy") : "—";

// Pré-remplissage de la VR à la création du devis. Doit rester en pleine
// précision décimale sur le prix HT des options pour égaler le calcul de
// référence côté API (DevisEloquentAdapter::calculerMontantOptionsInclVR,
// qui utilise un cast (float)) — LS-665.
export const calculerValeurRachatFinale = (vehicle, rateVR, TVA_INVERT) => {
  if (!(rateVR > 0)) {
    return "0";
  }
  var optionsCost = 0;
  vehicle.optionsNotIncluded.forEach((v) => {
    if (v.isInclVR) {
      optionsCost += parseFloat(v.priceHT) * (v.isInclTVA ? TVA_INVERT : 1);
    }
  });
  return (
    Math.round(
      (((vehicle.pricePublicTTC + optionsCost) * parseFloat(rateVR)) / 100) *
        100
    ) / 100
  );
};

const CalcVRBox = ({ quotation, rlChange, disabled }) => {
  const [state, dispatch] = useStore();

  var {
    vehicle,
    dur,
    km,
    repurchase,
    repurchase_data,
    rateVR,
    repurchaseValueFinal,
    kmPrice,
  } = quotation;

  var actions = repurchaseActions(state, dispatch);
  const { getTVAByCatalog } = useTVA();
  const { TVA_INVERT } = getTVAByCatalog(vehicle.catalog.uuid);

  const { grille, taux, modeResolution, isLoading, error } = useSimulatedRateVR(
    vehicle,
    dur,
    km
  );

  const modesResolution =
    state.constant.items?.FLOTTE?.MODES_RESOLUTION_VR || [];
  const modeLabel = modeResolution
    ? (
        modesResolution.find((m) => m.id === modeResolution) || {
          name: modeResolution,
        }
      ).name
    : null;

  // repurchase_data reste chargé : nécessaire au kmPrice par défaut.
  useEffect(() => {
    if (repurchase_data.length == 0 && !state.repurchase.isLoading) {
      actions.getRepurchasesFromVehicle(
        vehicle.brand.uuid,
        vehicle.brandModel.uuid,
        vehicle.brandModelFinish.fuelType.id
      );
    }
  }, [vehicle.uuid]);

  if (
    repurchase_data.length == 0 &&
    !state.repurchase.isLoading &&
    state.repurchase.items.find(
      (r) =>
        r.brand.uuid == vehicle.brand.uuid &&
        r.brandModels.find((b) => b.uuid == vehicle.brandModel.uuid)
    )
  ) {
    rlChange({
      target: {
        name: "repurchase_data",
        value: JSON.parse(JSON.stringify(state.repurchase.items)),
      },
    });
  }

  var rateVRCalc = taux !== null ? taux : "0";

  var repurchaseValueFinalCalc = calculerValeurRachatFinale(
    vehicle,
    rateVR,
    TVA_INVERT
  );

  // Auto-remplissage uniquement quand le taux n'a pas encore été fixé
  // (devis neuf ou taux remis à zéro) : un devis enregistré rouvert ne voit
  // ni son taux ni sa référence de grille écrasés.
  if (grille && taux !== null && rateVR === "0") {
    rlChange({ target: { name: "rateVR", value: rateVRCalc } });
    if (repurchase !== grille.uuid) {
      rlChange({ target: { name: "repurchase", value: grille.uuid } });
    }
  }
  if (
    grille &&
    repurchaseValueFinal === "0" &&
    repurchaseValueFinalCalc !== "0"
  ) {
    rlChange({
      target: {
        name: "repurchaseValueFinal",
        value: repurchaseValueFinalCalc,
      },
    });
  }

  useEffect(() => {
    // La grille résolue par l'API peut ne pas (encore) figurer dans
    // repurchase_data : on ne pousse le kmPrice par défaut que si on l'y trouve.
    const repurchaseRef = repurchase_data.find((r) => r.uuid == repurchase);
    if (repurchaseRef && (kmPrice == "-1" || kmPrice == "")) {
      rlChange({
        target: {
          name: "kmPrice",
          value: repurchaseRef.kmPrice,
        },
      });
    }
  }, [kmPrice, repurchase]);

  return (
    <>
      <div className="form-group">
        <label>Grille VR</label>
        {isLoading ? (
          <div className="vr-grid-box">
            <Loader size={24} />
          </div>
        ) : grille ? (
          <div className="vr-grid-box">
            <div className="vr-grid-name">{grille.name}</div>
            <div className="vr-grid-valid">
              {grille.validTo
                ? `Validité du ${formatDate(grille.validFrom)} au ${formatDate(
                    grille.validTo
                  )}`
                : `Validité depuis le ${formatDate(grille.validFrom)}`}
            </div>
            {modeLabel && (
              <span className="vr-mode-badge">Mode : {modeLabel}</span>
            )}
          </div>
        ) : null}
        {!isLoading && error && (
          <div className="alert alert-warning py-2 mb-0 mt-1">
            <i className="fa fa-exclamation-triangle mr-2" />
            {error}
          </div>
        )}
      </div>
      <InputGroupWithResetValue
        props={{
          name: "rateVR",
          symbol: "%",
          label: "Taux VR",
          defaultValue: rateVRCalc,
          required: true,
        }}
        data={{ rateVR }}
        resetDefault={(name, value) =>
          rlChange({
            target: {
              name,
              value,
            },
          })
        }
        inputChange={rlChange}
        disabled={disabled}
        defferChange
      />

      <InputGroupWithResetValue
        props={{
          name: "repurchaseValueFinal",
          symbol: "€",
          label: "Valeur de rachat TTC",
          defaultValue: repurchaseValueFinalCalc,
          required: true,
        }}
        data={{ repurchaseValueFinal }}
        resetDefault={(name, value) =>
          rlChange({
            target: {
              name,
              value,
            },
          })
        }
        inputChange={rlChange}
        disabled={disabled}
      />
      <Control
        label="Prix du KM supplémentaire"
        type="text"
        suffix={"€"}
        value={kmPrice}
        change={(e) =>
          rlChange({
            target: {
              name: "kmPrice",
              value: e.target.value,
            },
          })
        }
        disabled={disabled}
      />
    </>
  );
};

export default CalcVRBox;
